/**
 * helperV2.js — Standalone navigation, validation, normalization, and type-switching helpers
 * for Business Single Application V2.
 *
 * This file does NOT import from BusinessPeriodicReview/helper.js.
 * It uses ApplyBusinessPeriodicReview as a structural reference only.
 */

import _ from "lodash";
import { getExpandedFields, normalizeBooleanLikeValue, readValue, resolveFieldPath, shouldShowField, validateField } from "../../../../components/InputField/helper";
import { doGET } from "../../../../util/HttpUtil";
import { validatePincodeByCountry } from "../../components/PincodeField/helper";
import { calculateIndividualShareholderOwnershipTotal, getIndividualShareholderOwnershipError } from "../helpers/ownershipValidation";
import { hydrateBusinessV2PayloadFromApi } from "./compatibilityV2";

// ── Business Type Constants (mirrored from BPrime/businessTypeUtils.js) ──

export const BUSINESS_TYPE_VALUES = Object.freeze({
    CRYPTO_EXCHANGE_OTC_LIQUIDITY_REMITTANCE_FX_MSB: "MSB_Crypto_Exchange_OTC_Liquidity_Provider_Remittance_FX_Business",
    STOCK_BROKERAGE: "Stock_Brokerage",
    FUND_FAMILY_OFFICE: "Fund_Family_Office",
    FINANCIAL_INSTITUTION: "Financial_Institution",
    OTHER: "Other",
});

export const getBusinessTypeChecks = (selectedBusinessType = null) => {
    const selectedValue = Array.isArray(selectedBusinessType)
        ? selectedBusinessType[0]
        : selectedBusinessType;
    return {
        msbCheck: [
            BUSINESS_TYPE_VALUES.CRYPTO_EXCHANGE_OTC_LIQUIDITY_REMITTANCE_FX_MSB,
            BUSINESS_TYPE_VALUES.STOCK_BROKERAGE,
            BUSINESS_TYPE_VALUES.FINANCIAL_INSTITUTION,
        ].includes(selectedValue),
        cryptoExchangeCheck: selectedValue === BUSINESS_TYPE_VALUES.CRYPTO_EXCHANGE_OTC_LIQUIDITY_REMITTANCE_FX_MSB,
        fundAndFamilyCheck: selectedValue === BUSINESS_TYPE_VALUES.FUND_FAMILY_OFFICE,
        accountUpgradeCheck: selectedValue === BUSINESS_TYPE_VALUES.FINANCIAL_INSTITUTION,
    };
};

// ── V2 Subtypes ──

export const V2_TYPE = {
    PLUS: 1,
    PRIME: 2,
    UPGRADE: 3,
};

export const V2_TYPE_LABELS = {
    [V2_TYPE.PLUS]: "Business Plus",
    [V2_TYPE.PRIME]: "Business Prime",
    [V2_TYPE.UPGRADE]: "Business Prime Upgrade",
};

export const CONTROL_PRONG_OWNERSHIP_MESSAGE = "Please ensure at least one user is listed as either: a Control Prong Person, or a Shareholder with 25% or greater ownership. Please note: ownership by another entity/company does not qualify.";

const mergeFieldTypeOverride = (field = {}, type) => {
    const override = field?.typeOverrides?.[String(type)];
    if (!_.isPlainObject(override)) {
        return field;
    }

    const merged = {
        ...field,
        ...override,
    };

    if (field?.errorTypes || override?.errorTypes) {
        merged.errorTypes = {
            ...(field?.errorTypes || {}),
            ...(override?.errorTypes || {}),
        };
    }

    if (override?.options) {
        merged.options = override.options;
    }

    if (Number(type) === 1 && !Object.prototype.hasOwnProperty.call(override?.errorTypes || {}, 'required')) {
        const nextErrorTypes = { ...(merged?.errorTypes || {}) };
        delete nextErrorTypes.required;
        merged.errorTypes = Object.keys(nextErrorTypes).length ? nextErrorTypes : undefined;
    }

    return merged;
};

const getResolvedFieldTypes = (field = {}, type) => {
    const overrideTypes = field?.typeOverrides?.[String(type)]?.types;
    if (Array.isArray(overrideTypes)) {
        return overrideTypes;
    }
    return field?.types;
};

const isBusinessInformationFieldForType = (field = {}, normalizedType) => (
    normalizedType === V2_TYPE.UPGRADE
    && BUSINESS_INFORMATION_WIZARDS.includes(field?.wizard)
);

const isStructuralAutoSaveField = (field = {}) => (
    Boolean(field?.autoSave?.structural)
    || Boolean(field?.autoSave?.targetRecord)
    || Boolean(field?.autoSave?.savePath)
);

export const isFieldEnabledForType = (field = {}, type) => {
    if (field?.disabledReason) {
        return false;
    }

    const normalizedType = Number(type);
    const fieldTypes = getResolvedFieldTypes(field, normalizedType);

    if (!Array.isArray(fieldTypes)) {
        return true;
    }

    if (fieldTypes.length === 0) {
        if (isStructuralAutoSaveField(field)) {
            return true;
        }
        return false;
    }

    if (fieldTypes.includes(normalizedType)) {
        return true;
    }

    if (isBusinessInformationFieldForType(field, normalizedType)) {
        return fieldTypes.includes(V2_TYPE.PLUS) || fieldTypes.includes(V2_TYPE.PRIME);
    }

    return false;
};

export const resolveFieldsForType = (fields = [], type) => (
    (Array.isArray(fields) ? fields : [])
        .map((field) => mergeFieldTypeOverride(field, type))
        .filter((field) => Boolean(field) && isFieldEnabledForType(field, type))
);

export const createFieldDefinitionMap = (fields = []) => {
    if (!Array.isArray(fields)) {
        return {};
    }

    return fields.reduce((accumulator, fieldDefinition) => {
        if (!fieldDefinition || typeof fieldDefinition !== 'object') {
            return accumulator;
        }

        const keys = [
            fieldDefinition?.field,
            fieldDefinition?.fvBankProperty,
        ].filter((key) => typeof key === 'string' && key.trim());

        if (!keys.length) {
            return accumulator;
        }

        keys.forEach((key) => {
            accumulator[key] = fieldDefinition?.field
                ? fieldDefinition
                : {
                    ...fieldDefinition,
                    field: key,
                };
        });

        return accumulator;
    }, {});
};

// ── Wizard Constants ──

export const WIZARD = {
    BusinessInformation: "BusinessInformation",
    BusinessIdentity: "BusinessIdentity",
    ContactAndAddresses: "ContactAndAddresses",
    PeopleAndOwnership: "PeopleAndOwnership",
    BusinessActivity: "BusinessActivity",
    SelfDeclaration: "SelfDeclaration",
    BusinessType: "BusinessType",
    AccountActivity: "AccountActivity",
    MoneyServicesBusiness: "MoneyServicesBusiness",
    AssetsInvolvement: "AssetsInvolvement",
    FundFamilyOffices: "FundFamilyOffices",
    ComplianceDetails: "ComplianceDetails",
    Documents: "Documents",
    PayNow: "PayNow",
    ThankYou: "ThankYou",

    // Recovery states (outside main wizard)
    ExtraQuestions: "ExtraQuestions",
    ApplicationDocRedo: "ApplicationDocRedo",
    AskMoreMessage: "AskMoreMessage",
};

/**
 */
export const WIZARD_NUM = {
    BusinessInformation: 1,
    BusinessActivity: 2,
    BusinessIdentity: 3,
    ContactAndAddresses: 4,
    PeopleAndOwnership: 6,
    SelfDeclaration: 5,
    BusinessType: 7,
    AccountActivity: 9,
    MoneyServicesBusiness: 10,
    AssetsInvolvement: 11,
    FundFamilyOffices: 12,
    ComplianceDetails: 14,
    Documents: 15,
    PayNow: 16,
    ThankYou: Number.MAX_SAFE_INTEGER,

    ExtraQuestions: 17,
    ApplicationDocRedo: 17,
    AskMoreMessage: 0,
};

/**
 *
 *
 */
export const BUSINESS_INFORMATION_WIZARDS = Object.freeze([
    WIZARD.BusinessInformation,
    WIZARD.BusinessIdentity,
    WIZARD.ContactAndAddresses,
    WIZARD.PeopleAndOwnership,
]);

export const SPLIT_BUSINESS_INFORMATION_WIZARDS = Object.freeze([
    WIZARD.BusinessIdentity,
    WIZARD.ContactAndAddresses,
    WIZARD.PeopleAndOwnership,
]);

const PRIME_ACCOUNT_ACTIVITY_FIELDS_FROM_BUSINESS_ACTIVITY = new Set([
    "Primary_Business_Activities",
    "Business_Product_Services",
    "productsOrServices",
    "businessPurpose",
    "primaryCustomer",
    "revenueRange",
    "hasAuditStatements",
    "hasAnnualStatements",
    "hasFinancialForecast",
    "hasBusinessPlan",
    "businessInvolvements",
    "Illegal_Immoral_Disclosure",
    "Deposit_Reasons",
    "Payment_Reasons",
    "Deposit_Locations",
    "Payment_Locations",
    "internationalCustomers",
    "hasCoinConversionPlan",
    "hasCryptoConversionPlan",
]);

const PRIME_ACCOUNT_ACTIVITY_FIELD_ORDER = [
    "Primary_Business_Activities",
    "Business_Product_Services",
    "productsOrServices",
    "businessPurpose",
    "primaryCustomer",
    "revenueRange",
    "hasAuditStatements",
    "hasAnnualStatements",
    "hasFinancialForecast",
    "hasBusinessPlan",
    "businessInvolvements",
    "Illegal_Immoral_Disclosure",
    "Deposit_Reasons",
    "Payment_Reasons",
    "Deposit_Locations",
    "Payment_Locations",
    "hasCoinConversionPlan",
    "hasCryptoConversionPlan",
    "internationalCustomers",
    "Amount_Of_Initial_Deposit",
    "Average_Balance",
    "Number_Of_Monthly_Ach_Deposits",
    "Amount_Of_Monthly_Ach_Deposits",
    "Number_Of_Monthly_Domestic_Wire_Deposits",
    "Amount_Of_Monthly_Domestic_Wire_Deposits",
    "Number_Of_Monthly_International_Wire_Deposits",
    "Amount_Of_Monthly_International_Wire_Deposits",
    "Number_Of_Monthly_Ach_Payments",
    "Amount_Of_Monthly_Ach_Payments",
    "Number_Of_Monthly_Domestic_Wire_Payments",
    "Amount_Of_Monthly_Domestic_Wire_Payments",
    "Number_Of_Monthly_International_Wire_Payments",
    "Amount_Of_Monthly_International_Wire_Payments",
    "Expected_Amount_Monthly_Digital_Currency_Custody",
    "Count_Of_Monthly_Stablecoin_Conversions_Deposits",
    "Expected_Amount_Monthly_Incoming_USDC",
    "Count_Of_Monthly_Stablecoin_Conversions_Payments",
    "Expected_Amount_Monthly_Outgoing_USDC",
    "Count_Of_Monthly_Cryptocurrency_Conversions",
    "Amount_Of_Monthly_Cryptocurrency_Conversions",
];

const STABLECOIN_CONVERSION_ACTIVITY_FIELDS = new Set([
    "Number_Of_Monthly_Stablecoin_Conversions_Deposits",
    "Amount_Of_Monthly_Stablecoin_Conversions_Deposits",
    "Number_Of_Monthly_Stablecoin_Conversions_Payments",
    "Amount_Of_Monthly_Stablecoin_Conversions_Payments",
    "Count_Of_Monthly_Stablecoin_Conversions_Deposits",
    "Expected_Amount_Monthly_Incoming_USDC",
    "Count_Of_Monthly_Stablecoin_Conversions_Payments",
    "Expected_Amount_Monthly_Outgoing_USDC",
]);

const CRYPTO_CONVERSION_ACTIVITY_FIELDS = new Set([
    "Count_Of_Monthly_Cryptocurrency_Conversions",
    "Amount_Of_Monthly_Cryptocurrency_Conversions",
]);

export const shouldIncludeConversionPlanActivityField = (fieldName, formData = {}) => {
    if (!fieldName) return true;

    if (STABLECOIN_CONVERSION_ACTIVITY_FIELDS.has(fieldName)) {
        return normalizeBooleanLikeValue(formData?.hasCoinConversionPlan) === true;
    }

    if (CRYPTO_CONVERSION_ACTIVITY_FIELDS.has(fieldName)) {
        return normalizeBooleanLikeValue(formData?.hasCryptoConversionPlan) === true;
    }

    return true;
};

const PRIME_ACCOUNT_ACTIVITY_FIELD_ORDER_INDEX = new Map(
    PRIME_ACCOUNT_ACTIVITY_FIELD_ORDER.map((field, index) => [field, index]),
);

const shouldIncludeFieldForWizard = (field, wizard, type) => {
    const normalizedType = Number(type);
    const fieldWizard = field?.wizard;
    const fieldName = field?.field;

    if (!fieldWizard || !fieldName) return false;
    if (fieldWizard === wizard) return true;

    if (wizard === WIZARD.BusinessInformation) {
        return SPLIT_BUSINESS_INFORMATION_WIZARDS.includes(fieldWizard);
    }

    if ((normalizedType === V2_TYPE.PRIME || normalizedType === V2_TYPE.UPGRADE) && wizard === WIZARD.AccountActivity) {
        if (fieldWizard === WIZARD.BusinessActivity) {
            return PRIME_ACCOUNT_ACTIVITY_FIELDS_FROM_BUSINESS_ACTIVITY.has(fieldName);
        }
    }

    return false;
};

const sortFieldOrderForWizard = (fieldOrder, wizard, type) => {
    const normalizedType = Number(type);
    if ((normalizedType === V2_TYPE.PRIME || normalizedType === V2_TYPE.UPGRADE) && wizard === WIZARD.AccountActivity) {
        return [...fieldOrder].sort((left, right) => {
            const leftIndex = PRIME_ACCOUNT_ACTIVITY_FIELD_ORDER_INDEX.get(left);
            const rightIndex = PRIME_ACCOUNT_ACTIVITY_FIELD_ORDER_INDEX.get(right);
            return (leftIndex ?? Number.MAX_SAFE_INTEGER) - (rightIndex ?? Number.MAX_SAFE_INTEGER);
        });
    }

    return fieldOrder;
};

export const normalizeWizardSequenceForType = (type, sequence = []) => {
    return Array.isArray(sequence) ? [...sequence] : [];
};

const getBusinessTypeConfig = (jsonConfig = {}) => jsonConfig?.businessTypeConfig || {};
const getEntryContextConfig = (jsonConfig = {}) => jsonConfig?.entryContextConfig || {};

export const getEntryContextNameForType = (type, jsonConfig = {}) => {
    const normalizedType = Number(type);
    return getEntryContextConfig(jsonConfig)?.typeToContext?.[String(normalizedType)] || null;
};

const getEntryContextForType = (type, jsonConfig = {}) => {
    const contextName = getEntryContextNameForType(type, jsonConfig);
    if (!contextName) {
        return null;
    }

    return getEntryContextConfig(jsonConfig)?.contexts?.[contextName] || null;
};

export const getEntryContextIntroWizard = (type, jsonConfig = {}) => {
    const configuredIntroWizard = getEntryContextForType(type, jsonConfig)?.introWizard;
    if (configuredIntroWizard) {
        return configuredIntroWizard;
    }

    const normalizedType = Number(type);
    return (normalizedType === V2_TYPE.PLUS || normalizedType === V2_TYPE.PRIME)
        ? "BusinessPlusIntroduction"
        : null;
};

export const getSelectedBusinessType = (formData = {}, jsonConfig = {}) => {
    const businessField = getBusinessTypeConfig(jsonConfig)?.field || "businesses";
    const selectedValue = _.get(formData, businessField);
    return Array.isArray(selectedValue) ? selectedValue[0] : selectedValue;
};

export const getBusinessTypeRule = (formData = {}, jsonConfig = {}) => {
    const selectedValue = getSelectedBusinessType(formData, jsonConfig);
    const options = getBusinessTypeConfig(jsonConfig)?.options || [];
    return options.find((option) => option?.value === selectedValue) || null;
};

export const doesSelectedBusinessTypeRequirePrime = (formData = {}, jsonConfig = {}) => (
    Boolean(getBusinessTypeRule(formData, jsonConfig)?.requiresPrime)
);

const isWizardEnabledByBusinessTypeRule = (wizardName, businessTypeRule = null) => {
    if (!businessTypeRule) return false;

    if (Array.isArray(businessTypeRule?.enabledWizards)) {
        return businessTypeRule.enabledWizards.includes(wizardName);
    }

    switch (wizardName) {
        case WIZARD.MoneyServicesBusiness:
            return Boolean(businessTypeRule?.showMoneyServicesBusiness);
        case WIZARD.AssetsInvolvement:
            return Boolean(businessTypeRule?.showDigitalAssets);
        case WIZARD.FundFamilyOffices:
            return Boolean(businessTypeRule?.showFundFamilyOffices);
        case WIZARD.ComplianceDetails:
            return Boolean(businessTypeRule?.showComplianceDetails);
        default:
            return false;
    }
};

// ── Wizard Navigation (JSON-driven) ──

/**
 * Evaluate whether a conditional wizard should be shown based on form data and JSON conditions.
 */
const evaluateWizardCondition = (condition, formData, type) => {
    if (!condition) return true;

    // Type restriction
    if (condition.onlyForTypes && !condition.onlyForTypes.includes(type)) {
        return false;
    }

    const showWhen = condition.showWhen;
    if (!showWhen) return true;

    return evaluateCondition(showWhen, formData);
};

const evaluateCondition = (cond, formData) => {
    if (!cond) return true;

    // OR condition
    if (cond.or) {
        return cond.or.some((sub) => evaluateCondition(sub, formData));
    }

    // AND condition
    if (cond.and) {
        return cond.and.every((sub) => evaluateCondition(sub, formData));
    }

    // Leaf condition
    const { fieldName, operator, values } = cond;
    const fieldValue = _.get(formData, fieldName);
    const expectedValues = values || [];

    switch (operator) {
        case "INCLUDES": {
            if (Array.isArray(fieldValue)) {
                return expectedValues.some((v) => fieldValue.includes(v));
            }
            return expectedValues.some((v) => fieldValue === v);
        }
        case "EQUALS":
            return fieldValue === (expectedValues[0] ?? null);
        case "NOT_EQUALS":
            return fieldValue !== (expectedValues[0] ?? null);
        case "NOT_EXISTS":
            return fieldValue === undefined || fieldValue === null || fieldValue === "";
        case "EXISTS":
            return fieldValue !== undefined && fieldValue !== null && fieldValue !== "";
        default:
            return true;
    }
};

/**
 * Wizards hidden for broker-sponsored (brokerId) applications: the payment step (PayNow) and the
 * Terms & Conditions step (SelfDeclaration) the primary owner accepts from the verification link,
 * not the broker admin. JSON-configurable via `sponsoredHiddenWizards`; defaults to [PayNow,
 * SelfDeclaration]. Used by both the navigation sequence (resolveActiveWizards) and the stepper
 * display sequence so the two never disagree about which steps a broker can reach.
 */
export const getSponsoredHiddenWizards = (jsonConfig) => (
    Array.isArray(jsonConfig?.sponsoredHiddenWizards)
        ? jsonConfig.sponsoredHiddenWizards
        : [WIZARD.PayNow, WIZARD.SelfDeclaration]
);

/**
 * Resolve the active wizard sequence for a given type and current form data.
 * Filters out conditional wizards that should not be shown.
 */
export const resolveActiveWizards = (type, formData, jsonConfig) => {
    const typeKey = String(type);
    const fullSequence = normalizeWizardSequenceForType(type, jsonConfig?.wizardSequenceByType?.[typeKey] || []);
    const conditions = jsonConfig?.wizardConditions || {};
    const businessTypeRule = getBusinessTypeRule(formData, jsonConfig);

    // Wizards hidden for broker-sponsored applications (e.g. PayNow, and the
    // SelfDeclaration / Terms & Conditions step that the customer must accept
    // themselves during KYC, not the broker admin) are JSON-configurable via
    // `sponsoredHiddenWizards`. When the key is absent we hide the payment and
    // Terms steps for broker-sponsored applications: brokers do not collect
    // payment, and the primary owner accepts terms from the verification link.
    // Broker customers are never charged via complyAi (fixed business rule), so
    // an application is broker-sponsored whenever it carries a brokerId.
    const isSponsoredApplication = Boolean(formData?.brokerId);
    const sponsoredHiddenWizards = getSponsoredHiddenWizards(jsonConfig);
    let sequenceForPaymentPolicy = isSponsoredApplication
        ? fullSequence.filter((wizardName) => !sponsoredHiddenWizards.includes(wizardName))
        : fullSequence;
    // Business Prime upgrades (type 3) are existing Business Plus customers and were
    // never charged. They have no payment step at all — they submit on the Terms &
    // Conditions (SelfDeclaration) step — so PayNow is dropped from their sequence.
    if (Number(type) === V2_TYPE.UPGRADE) {
        sequenceForPaymentPolicy = sequenceForPaymentPolicy.filter((wizardName) => wizardName !== WIZARD.PayNow);
    }

    // A `requiresPrime` answer collapses a Plus flow down to [Intro, BusinessType] so a direct
    // applicant can be re-routed to Prime. Broker-sponsored applications are Plus-only — the partner
    // channel never routes to Prime — and the document-first config wires the Prime rule to
    // `isFinancialInstitution`, so a stray "Yes" (or an extracted/true value) would otherwise collapse
    // the broker flow and strand the applicant on the intro. Brokers attest non-FI status instead
    // (see BusinessInformationVersion2's not-MSB confirmation), so never collapse a sponsored app.
    if (Number(type) === V2_TYPE.PLUS && !isSponsoredApplication && businessTypeRule?.requiresPrime) {
        return sequenceForPaymentPolicy.filter((wizardName) => (
            wizardName === getEntryContextIntroWizard(type, jsonConfig)
            || wizardName === WIZARD.BusinessType
        ));
    }

    return sequenceForPaymentPolicy.filter((wizardName) => {
        const condition = conditions[wizardName];
        if (condition?.businessTypeAccess === "enabled") {
            if (!isWizardEnabledByBusinessTypeRule(wizardName, businessTypeRule)) {
                return false;
            }
        }
        if (!condition) return true;
        return evaluateWizardCondition(condition, formData, type);
    });
};

/**
 * Get the next wizard step.
 */
export const getNextStepV2 = (currentWizard, type, formData, jsonConfig) => {
    const active = resolveActiveWizards(type, formData, jsonConfig);
    const idx = active.indexOf(currentWizard);

    if (idx === -1) {
        // Current wizard not in sequence — return first wizard
        return { wizard: active[0] || WIZARD.BusinessType };
    }

    if (idx < active.length - 1) {
        return { wizard: active[idx + 1] };
    }

    // Already at the last wizard (e.g. ThankYou)
    return { wizard: currentWizard };
};

/**
 * Get the previous wizard step.
 */
export const handlePrevV2 = (currentWizard, type, formData, jsonConfig) => {
    const active = resolveActiveWizards(type, formData, jsonConfig);
    const idx = active.indexOf(currentWizard);

    if (idx <= 0) {
        // Already at the first wizard
        return { wizard: active[0] || WIZARD.BusinessType };
    }

    return { wizard: active[idx - 1] };
};

/**
 * Get the first wizard for a given type.
 */
export const getFirstWizard = (type, jsonConfig) => {
    const typeKey = String(type);
    const sequence = normalizeWizardSequenceForType(type, jsonConfig?.wizardSequenceByType?.[typeKey] || []);
    return sequence[0] || WIZARD.BusinessType;
};

/**
 * Get wizard label from JSON config.
 */
export const getWizardLabel = (wizard, jsonConfig) => {
    return jsonConfig?.wizardLabels?.[wizard] || wizard;
};

/**
 * Get the wizard step number (for progress display).
 * Returns 1-based index within the active sequence.
 */
export const getWizardStepNumber = (wizard, type, formData, jsonConfig) => {
    const active = resolveActiveWizards(type, formData, jsonConfig);
    const idx = active.indexOf(wizard);
    return idx >= 0 ? idx + 1 : 0;
};

/**
 * Get total step count (excluding ThankYou).
 */
export const getTotalSteps = (type, formData, jsonConfig) => {
    const active = resolveActiveWizards(type, formData, jsonConfig);
    return active.filter((w) => w !== WIZARD.ThankYou).length;
};

// ── Data Fetching ──

export const fetchBusinessDetailData = async (formId) => {
    try {
        const response = await doGET(`/api/business/detail?id=${formId}`);
        return hydrateBusinessV2PayloadFromApi(response.data ?? {});
    } catch (error) {
        console.error("fetchBusinessDetailData failed:", error);
        return {};
    }
};

// ── Primary User Helpers ──

export const getPrimaryIndividualIndex = (data = {}) => {
    if (!Array.isArray(data?.individuals)) return -1;
    const idx = data.individuals.findIndex((ind) => ind?.isPrimaryUser);
    if (idx >= 0) return idx;
    return data.individuals.length ? 0 : -1;
};

export const movePrimaryUserToFront = (list = []) => {
    if (!Array.isArray(list)) return [];
    try {
        const idx = list.findIndex((item) => item?.isPrimaryUser);
        if (idx <= 0) return list;
        return [list[idx], ...list.slice(0, idx), ...list.slice(idx + 1)];
    } catch (err) {
        console.error("movePrimaryUserToFront failed:", err);
        return Array.isArray(list) ? [...list] : [];
    }
};

// ── Data Normalizers ──

/**
 * Ensure primary user is first in the individuals array and has correct defaults.
 */
export const ensurePrimaryUser = (data) => {
    if (!data || !Array.isArray(data.individuals)) return data;

    const result = { ...data };
    result.individuals = movePrimaryUserToFront([...result.individuals]);

    // Ensure first individual is primary
    if (result.individuals.length > 0 && !result.individuals[0].isPrimaryUser) {
        result.individuals[0] = { ...result.individuals[0], isPrimaryUser: true };
    }

    // Primary user must be authorized user with NoGroup access
    if (result.individuals.length > 0) {
        const primary = result.individuals[0];
        if (!primary.isAuthorizedUser) {
            result.individuals[0] = { ...primary, isAuthorizedUser: true };
        }
        if (!primary.accessType) {
            result.individuals[0] = { ...result.individuals[0], accessType: "NoGroup" };
        }
    }

    return result;
};

export const ADDRESS_TYPES = {
    REGISTERED: "REGISTERED",
    MAILING: "MAILING",
    PRINCIPAL: "PRINCIPAL",
};

/**
 * Ensure address rows exist for registered and mailing addresses.
 */
export const ensureBusinessAddresses = (data) => {
    if (!data) return data;
    const result = { ...data };

    if (!Array.isArray(result.addressList)) {
        result.addressList = [];
    }

    const hasType = (type) => result.addressList.some((a) => a?.type === type);

    if (!hasType(ADDRESS_TYPES.REGISTERED)) {
        result.addressList.push({ type: ADDRESS_TYPES.REGISTERED });
    }
    if (!hasType(ADDRESS_TYPES.MAILING)) {
        result.addressList.push({ type: ADDRESS_TYPES.MAILING });
    }

    return result;
};

/**
 * Apply address mirror rules (same-as toggles for registered/mailing).
 */
export const applyAddressMirrorRules = (data) => {
    if (!data || !Array.isArray(data.addressList)) return data;

    const result = { ...data, addressList: [...data.addressList] };
    const principal = result.address || {};

    if (result.isRegisteredAddressSame && principal) {
        const regIdx = result.addressList.findIndex((a) => a?.type === ADDRESS_TYPES.REGISTERED);
        if (regIdx >= 0) {
            result.addressList[regIdx] = {
                ...result.addressList[regIdx],
                street: principal.street,
                buildingNumber: principal.buildingNumber,
                city: principal.city,
                state: principal.state,
                country: principal.country,
                pinCode: principal.pinCode,
            };
        }
    }

    if (result.isMailingAddressSame && principal) {
        const mailIdx = result.addressList.findIndex((a) => a?.type === ADDRESS_TYPES.MAILING);
        if (mailIdx >= 0) {
            result.addressList[mailIdx] = {
                ...result.addressList[mailIdx],
                street: principal.street,
                buildingNumber: principal.buildingNumber,
                city: principal.city,
                state: principal.state,
                country: principal.country,
                pinCode: principal.pinCode,
            };
        }
    }

    return result;
};

/**
 * Normalize data before save: clear entities if not applicable, clean phone country codes.
 */
export const normalizeBeforeSave = (data) => {
    if (!data) return data;
    let result = { ...data };

    // Clear entities when hasEntity is false
    if (result.hasEntity === false && Array.isArray(result.entities) && result.entities.length > 0) {
        result.entities = [];
    }

    // Clear phone country codes when phone is empty
    if (!result.mobilePhone) {
        result.mobileCountryCode = undefined;
    }
    if (!result.landlinePhone) {
        result.landlineCountryCode = undefined;
    }

    return result;
};

// ── Type Switching ──

/**
 * Check if type switching is allowed based on current state and JSON config.
 */
export const isTypeSwitchAllowed = (applicationForm, jsonConfig, requestedNextType = null) => {
    const currentType = Number(applicationForm?.type);
    const nextType = Number(requestedNextType);
    const hasRequestedTargetType = Number.isFinite(nextType) && nextType > 0;
    const lockedWhenField = getEntryContextConfig(jsonConfig)?.lockedWhenField || "cyclosUserId";

    if (applicationForm?.[lockedWhenField]) return false;

    const entryContext = getEntryContextForType(currentType, jsonConfig);
    const contextSwitchConfig = entryContext?.typeSwitch;
    const usesEntryContextSwitchConfig = _.isPlainObject(contextSwitchConfig);

    if (usesEntryContextSwitchConfig) {
        if (!contextSwitchConfig?.allowBeforeLock) return false;
        const allowedTargetTypes = Array.isArray(contextSwitchConfig?.allowedTargetTypes)
            ? contextSwitchConfig.allowedTargetTypes.map((typeValue) => Number(typeValue))
            : [];

        if (!hasRequestedTargetType) {
            return allowedTargetTypes.length > 0;
        }

        return allowedTargetTypes.includes(nextType);
    }

    if (!jsonConfig?.lockRules?.allowTypeSwitchBeforeLock) return false;

    const allowedTypes = jsonConfig?.lockRules?.allowTypeSwitchTypes || [];
    if (!allowedTypes.includes(currentType)) return false;

    if (!hasRequestedTargetType) {
        return true;
    }

    const allowedTransitions = Array.isArray(jsonConfig?.typeSwitchRules?.allowedTransitions)
        ? jsonConfig.typeSwitchRules.allowedTransitions
        : [];

    return allowedTransitions.some(([fromType, toType]) => (
        Number(fromType) === currentType && Number(toType) === nextType
    ));
};

/**
 * Clean up fields when switching between types.
 */
export const cleanupFieldsForTypeSwitch = (data, fromType, toType, jsonConfig) => {
    const transitionKey = `${fromType}->${toType}`;
    const fieldsToClean = getEntryContextConfig(jsonConfig)?.transitions?.[transitionKey]?.cleanupFields
        || jsonConfig?.typeSwitchRules?.cleanupFieldsOnSwitch?.[transitionKey]
        || [];

    if (fieldsToClean.length === 0) return data;

    const result = { ...data };
    fieldsToClean.forEach((fieldPath) => {
        _.unset(result, fieldPath);
    });

    return result;
};

// ── Locking ──

/**
 * Check if the application is locked (Plus account already created).
 */
export const isApplicationLocked = (applicationForm, jsonConfig = {}) => {
    const lockedWhenField = getEntryContextConfig(jsonConfig)?.lockedWhenField || "cyclosUserId";
    return !!applicationForm?.[lockedWhenField];
};

// ── Validation Helpers ──

/**
 * Get visible fields for the current wizard, respecting type[] and showIf.
 */
export const getVisibleWizardFieldOrder = ({ fields = [], wizard, data, type, skipField = () => false } = {}) => {
    const normalizedType = Number(type || data?.type);
    const seenFields = new Set();

    const visibleFields = getExpandedFields(fields, data)
        .filter((field) => {
            const arrayIndexMatch = typeof field?.field === "string"
                ? field.field.match(/\[(\d+)\]/)
                : null;
            const arrayIndexing = arrayIndexMatch ? Number(arrayIndexMatch[1]) : null;
            const resolvedField = mergeFieldTypeOverride(field, normalizedType);

            if (!shouldIncludeFieldForWizard(resolvedField, wizard, normalizedType)) return false;
            if (!resolvedField?.uiType) return false;
            if (skipField(resolvedField, arrayIndexing)) return false;
            if (!shouldIncludeConversionPlanActivityField(resolvedField?.field, data)) return false;
            if (!shouldShowField(data, resolvedField?.showIf, arrayIndexing)) return false;
            // Filter by type
            if (!isFieldEnabledForType(field, normalizedType)) return false;
            return true;
        })
        .map((field) => field.field)
        .filter((fieldPath) => {
            if (!fieldPath || seenFields.has(fieldPath)) return false;
            seenFields.add(fieldPath);
            return true;
        });

    return sortFieldOrderForWizard(visibleFields, wizard, normalizedType);
};

/**
 * Validate ownership totals: individual shareholder percent cannot exceed 100.
 */
export const validateOwnershipTotals = (data) => {
    const errors = {};
    const individuals = data?.individuals || [];
    const ownershipError = getIndividualShareholderOwnershipError(individuals);

    if (ownershipError) {
        errors._ownershipTotal = ownershipError;
    }

    return errors;
};

/**
 * Validate Control Prong rules: at least one individual must be a Control Prong
 * or a shareholder with 25% or more ownership.
 */
export const validateControlPersonRules = (data) => {
    const errors = {};
    const individuals = data?.individuals || [];

    const hasControlProngOrQualifiedOwner = individuals.some((ind) => (
        ind?.isControlPerson === true
        || (ind?.isShareHolder === true && (parseFloat(ind?.percentOwner) || 0) >= 25)
    ));

    if (!hasControlProngOrQualifiedOwner) {
        errors._controlPerson = CONTROL_PRONG_OWNERSHIP_MESSAGE;
    }

    return errors;
};

/**
 * Validate no duplicate mobile phone or email within the application.
 */
export const validateDuplicateContacts = (data) => {
    const errors = {};
    const individuals = data?.individuals || [];

    const phoneSeen = new Set();
    const emailSeen = new Set();

    individuals.forEach((ind, idx) => {
        // Check phone
        if (ind?.mobilePhone && ind?.mobileCountryCode) {
            const phoneKey = `${ind.mobileCountryCode}-${ind.mobilePhone}`;
            if (phoneSeen.has(phoneKey)) {
                errors[`individuals[${idx}].mobilePhone`] = "Duplicate mobile phone number.";
            }
            phoneSeen.add(phoneKey);
        }

        // Check email
        if (ind?.email) {
            const emailKey = ind.email.toLowerCase().trim();
            if (emailSeen.has(emailKey)) {
                errors[`individuals[${idx}].email`] = "Duplicate email address.";
            }
            emailSeen.add(emailKey);
        }
    });

  return errors;
};

export const getFieldErrorValue = (errors = {}, fieldPath = "") => {
    if (!fieldPath || !errors || typeof errors !== "object") {
        return undefined;
    }

    if (Object.prototype.hasOwnProperty.call(errors, fieldPath)) {
        return errors[fieldPath];
    }

    return _.get(errors, fieldPath);
};

export const omitFieldPathErrors = (errors = {}, fieldPaths = []) => {
    if (!errors || typeof errors !== "object") {
        return errors;
    }

    const nextErrors = _.cloneDeep(errors);

    (Array.isArray(fieldPaths) ? fieldPaths : []).forEach((fieldPath) => {
        if (!fieldPath) return;

        if (_.has(nextErrors, fieldPath)) {
            _.unset(nextErrors, fieldPath);
        }

        if (Object.prototype.hasOwnProperty.call(nextErrors, fieldPath)) {
            delete nextErrors[fieldPath];
        }
    });

    return nextErrors;
};

export const getFirstErrorInFieldOrder = (fieldOrder = [], errors = {}) => {
    for (const fieldPath of fieldOrder) {
        if (!fieldPath) continue;
        const message = getFieldErrorValue(errors, fieldPath);
        if (message) {
            return {
                fieldPath,
                message,
            };
        }
    }

    return null;
};

const toErrorTemplateKey = (field) => (typeof field === 'string' ? field.replace(/\[\d+\]/g, '[]') : field);

// Orders resolved field paths to match the ON-SCREEN render order defined by the served
// config's uiSections, instead of the raw config declaration order. The sectioned step
// (BusinessV2SectionedFieldStep.resolveSections) renders sections in uiSections order and,
// within a section, keeps the fields in visibleFieldOrder order; fields not owned by any
// section render last. Error surfacing (toast + focus) walks this list so the FIRST message
// shown is the first invalid field a user actually sees — e.g. an empty Mobile Phone in the
// "Contact" section is reported before a too-long "Building and number" in the "Principal
// place of business" section, even though the config declares address fields first.
// Every address block renders street→building→city→state→zip→country regardless of how the config
// lists the leaves. Normalise a TEMPLATE-path list to that order, per address prefix, so principal,
// registered/mailing and per-person addresses all surface errors top-to-bottom as the user sees them.
const ERROR_ADDRESS_FIELD_ORDER = ['street', 'buildingNumber', 'city', 'state', 'pinCode', 'country'];
const errorAddressLeaf = (field) => {
    const p = String(field);
    const leaf = p.includes('.') ? p.slice(p.lastIndexOf('.') + 1) : p;
    return ERROR_ADDRESS_FIELD_ORDER.includes(leaf) ? leaf : null;
};
const normalizeAddressLeafOrder = (templatePaths) => {
    const result = [...templatePaths];
    const byPrefix = new Map();
    result.forEach((path, index) => {
        const leaf = errorAddressLeaf(path);
        if (!leaf) return;
        const prefix = String(path).slice(0, String(path).length - leaf.length);
        if (!byPrefix.has(prefix)) byPrefix.set(prefix, []);
        byPrefix.get(prefix).push(index);
    });
    byPrefix.forEach((indices) => {
        if (indices.length < 2) return;
        const sorted = indices
            .map((i) => result[i])
            .sort((a, b) => ERROR_ADDRESS_FIELD_ORDER.indexOf(errorAddressLeaf(a)) - ERROR_ADDRESS_FIELD_ORDER.indexOf(errorAddressLeaf(b)));
        indices.forEach((pos, k) => { result[pos] = sorted[k]; });
    });
    return result;
};

// THE single source of truth for the order in which any V2 step surfaces errors (toast + focus) — it
// mirrors the on-screen layout: sections in uiSections order, and WITHIN a section the fields in
// visibleFieldOrder order, exactly as BusinessV2SectionedFieldStep.resolveSections renders them (it
// filters visibleFieldOrder per section). Ungrouped fields keep their order and sort last. Finally
// every address block is normalised to street→building→… since the cards always render it that way.
// Every step's checkNext walks errors through this, so ordering stays consistent across all steps.
export const orderFieldPathsByUiSections = (fieldPaths = [], uiSections = []) => {
    if (!Array.isArray(fieldPaths) || fieldPaths.length === 0) return Array.isArray(fieldPaths) ? fieldPaths : [];
    if (!Array.isArray(uiSections) || uiSections.length === 0) return normalizeAddressLeafOrder(fieldPaths);

    const remaining = [...fieldPaths];
    const ordered = [];

    uiSections.forEach((section) => {
        const sectionTemplates = new Set(
            (Array.isArray(section?.fields) ? section.fields : []).map(toErrorTemplateKey),
        );
        if (sectionTemplates.size === 0) return;
        for (let i = 0; i < remaining.length;) {
            if (sectionTemplates.has(toErrorTemplateKey(remaining[i]))) {
                ordered.push(remaining[i]);
                remaining.splice(i, 1);
            } else {
                i += 1;
            }
        }
    });

    // Ungrouped fields (no owning section) keep their original relative order and render last.
    return normalizeAddressLeafOrder([...ordered, ...remaining]);
};

export const mergeClearedFieldErrors = (existingErrors = {}, fieldPathsToClear = [], nextErrors = {}) => {
    const cleanedErrors = omitFieldPathErrors(existingErrors || {}, fieldPathsToClear || []);
    return {
        cleanedErrors,
        mergedErrors: {
            ...cleanedErrors,
            ...(nextErrors || {}),
        },
    };
};

export const mapRuleErrorsToFieldErrors = (ruleErrors = [], { prioritizeActivityFields = false } = {}) => (
    (Array.isArray(ruleErrors) ? ruleErrors : []).reduce((acc, error) => {
        const fields = prioritizeActivityFields
            ? (
                Array.isArray(error?.activityFields) && error.activityFields.length
                    ? error.activityFields
                    : [error?.field]
            )
            : (
                Array.isArray(error?.activityFields) && error.activityFields.length
                    ? [...error.activityFields, error.field]
                    : [error?.field]
            );

        fields.filter(Boolean).forEach((field) => {
            acc[field] = error?.message;
        });

        return acc;
    }, {})
);

/**
 * Validate shareholder percentages.
 */
export const validateShareholderPercentages = (data) => {
    const errors = {};
    const individuals = data?.individuals || [];

    individuals.forEach((ind, idx) => {
        if (ind?.isShareHolder) {
            const pct = parseFloat(ind.percentOwner);
            if (isNaN(pct) || pct <= 0 || pct > 100) {
                errors[`individuals[${idx}].percentOwner`] = "Shareholder percentage must be greater than 0 and cannot exceed 100.";
            }
        }
    });

    return errors;
};

/**
 * Validate US SSN requirement: US nationality or US address requires SSN.
 */
export const validateUsSsnRequirement = (data) => {
    const errors = {};
    const individuals = data?.individuals || [];

    individuals.forEach((ind, idx) => {
        const isUsNational = ind?.nationality === "US" || ind?.nationality === "United States";
        const isUsAddress = ind?.address?.country === "US" || ind?.address?.country === "United States";

        if ((isUsNational || isUsAddress) && !ind?.ssn) {
            errors[`individuals[${idx}].ssn`] = "SSN is required for US nationals or residents.";
        }
    });

    return errors;
};

/**
 * Validate account activity cross-field rules.
 */
export const validateAccountActivityCrossField = (data, type) => {
    const errors = {};
    // Account activity required fields
    // todo @sanyam revisit it
    const requiredFields = [
        // "Average_Balance",
        // "Number_Of_Monthly_Ach_Deposits",
        // "Amount_Of_Monthly_Ach_Deposits",
        // "Number_Of_Monthly_Domestic_Wire_Deposits",
        // "Amount_Of_Monthly_Domestic_Wire_Deposits",
    ];

    for (const field of requiredFields) {
        const value = data?.[field];
        if (value === undefined || value === null || value === "") {
            errors[field] = "This field is required.";
        }
    }

    return errors;
};

// ── Field Expansion & Error Calculation ──

export const getArrayBase = (fieldPath) => {
    const m = fieldPath.match(/^([^[]+)\[\]/);
    return m ? m[1] : null;
};

export const expandFieldInstances = (field, data) => {
    const path = field?.field;
    if (!path) return [];

    if (!path.includes("[]")) {
        const resolved = resolveFieldPath(path, null);
        return [{ arrayIndexing: null, resolvedFieldPath: resolved }];
    }

    const arrayBase = getArrayBase(path);
    const arr = readValue(data, arrayBase) || [];
    const out = [];
    for (let i = 0; i < arr.length; i++) {
        out.push({
            arrayIndexing: i,
            resolvedFieldPath: resolveFieldPath(path, i),
        });
    }
    return out;
};

export const isEmptyValue = (v) =>
    v === undefined ||
    v === null ||
    (typeof v === "string" && v.trim() === "") ||
    (Array.isArray(v) && v.length === 0);

const hasAnyError = (value) => {
    if (!value) return false;
    if (typeof value === "string") return value.trim() !== "";
    if (Array.isArray(value)) return value.some(hasAnyError);
    if (typeof value === "object") return Object.values(value).some(hasAnyError);
    return false;
};

export const clearErrorsForFields = ({ setErrors, fields = [], setHasError } = {}) => {
    if (typeof setErrors !== "function" || !Array.isArray(fields) || fields.length === 0) {
        return;
    }

    let snapshot = undefined;

    setErrors((prevErrors) => {
        if (!prevErrors || typeof prevErrors !== "object") {
            snapshot = prevErrors;
            return prevErrors;
        }

        const nextErrors = _.cloneDeep(prevErrors);
        let changed = false;

        fields.forEach((fieldPath) => {
            if (!fieldPath) return;
            if (_.has(nextErrors, fieldPath)) {
                _.unset(nextErrors, fieldPath);
                changed = true;
                return;
            }
            if (Object.prototype.hasOwnProperty.call(nextErrors, fieldPath)) {
                delete nextErrors[fieldPath];
                changed = true;
            }
        });

        snapshot = changed ? nextErrors : prevErrors;
        return changed ? nextErrors : prevErrors;
    });

    if (typeof setHasError === "function") {
        setHasError(hasAnyError(snapshot));
    }
};

/**
 * Calculate initial errors for the current wizard step (same pattern as periodic review).
 */
export const collectVisibleFieldErrors = ({ data, step, iPlusFields, errors, type, shouldValidateField }) => {
    if (!Array.isArray(iPlusFields) || !step?.wizard) {
        return {
            fieldErrors: {},
            visibleResolvedPaths: [],
        };
    }

    const initialErrors = {};
    const shownErrors = {};
    const visibleResolvedPaths = new Set();
    const normalizedType = Number(type || data?.type);

    const stepFields = iPlusFields.filter((f) => {
        const resolvedField = mergeFieldTypeOverride(f, normalizedType);
        if (!shouldIncludeFieldForWizard(resolvedField, step?.wizard, normalizedType)) return false;
        if (!resolvedField?.uiType) return false;
        if (!isFieldEnabledForType(f, normalizedType)) return false;
        return true;
    });

    stepFields.forEach((field) => {
        const instances = expandFieldInstances(field, data);
        if (instances.length === 0) return;

        instances.forEach(({ arrayIndexing, resolvedFieldPath }) => {
            const isVisible = shouldShowField(data, field.showIf, arrayIndexing);
            if (!isVisible) return;

            const shouldValidate = shouldValidateField
                ? shouldValidateField(field, { arrayIndexing, resolvedFieldPath, data })
                : true;
            if (!shouldValidate) return;

            visibleResolvedPaths.add(resolvedFieldPath);

            const value = readValue(data, resolvedFieldPath, arrayIndexing);
            const forceRequired = field?.field === 'addressList[].street';
            const requiresCheckedValue = field?.type === 'boolean'
                && (field?.uiType === 'checkbox' || field?.uiType === 'termsAcknowledgement');
            // A date field holds an 8-digit YYYYMMDD string once set. Any other value
            // (a 0/epoch, "", null, or an unparseable value) renders as an EMPTY picker,
            // but isEmptyValue treats a numeric 0 as present — so the required check would
            // wrongly pass (e.g. "Date company registered" skippable). Treat a
            // non-YYYYMMDD date value as empty so the required gate catches it.
            const isEmptyDate = field?.uiType === 'date'
                && !/^\d{8}$/.test(String(value ?? ''));

            if (field.required || forceRequired) {
                const empty = requiresCheckedValue
                    ? value !== true
                    : (isEmptyValue(value) || isEmptyDate) && value !== false;
                if (empty && !getFieldErrorValue(errors, resolvedFieldPath)) {
                    const addressType = forceRequired && typeof arrayIndexing === 'number'
                        ? data?.addressList?.[arrayIndexing]?.type
                        : null;
                    const contextualStreetError = addressType === 'REGISTERED'
                        ? 'Please enter Street Name for Registered Address of Business.'
                        : addressType === 'MAILING'
                            ? 'Please enter Street Name for Mailing Address of Business.'
                            : null;

                    initialErrors[resolvedFieldPath] =
                        contextualStreetError || field?.errorTypes?.required || field?.error || "This field is required.";
                }
            }

            const err = validateField(field, value, data);
            if (err) {
                shownErrors[resolvedFieldPath] = err;
            }

            // Country-specific postal-code check. validateField only knows length/regex on the
            // field itself, not the per-country format, so a wrong-for-the-country zip (e.g. "3232"
            // for India, which needs 6 digits) passes the generic gate and the user could Continue —
            // even though PincodeField flags it inline. Mirror that check here so the gate blocks it.
            // The country lives at `${parentField}.country` (same source PincodeField reads). Fails
            // open if the postal-code master has not loaded yet (matches the field's own behaviour).
            if (field?.uiType === 'pinCode' && !isEmptyValue(value)) {
                const parentPath = resolveFieldPath(field?.parentField, arrayIndexing);
                const countryPath = parentPath
                    ? `${parentPath}.country`
                    : String(resolvedFieldPath).replace(/\.pinCode$/, '.country');
                const countryValue = readValue(data, countryPath, arrayIndexing);
                if (countryValue && !validatePincodeByCountry(countryValue, value)) {
                    // Name the address block the zip belongs to (same way the street error does), so a
                    // toast detached from the field says WHICH zip — registered vs mailing vs principal.
                    const zipAddressType = typeof arrayIndexing === 'number'
                        && /^addressList\[/.test(String(field?.field))
                        ? data?.addressList?.[arrayIndexing]?.type
                        : null;
                    shownErrors[resolvedFieldPath] = zipAddressType === 'REGISTERED'
                        ? 'Please enter a valid zip code for Registered Address of Business.'
                        : zipAddressType === 'MAILING'
                            ? 'Please enter a valid zip code for Mailing Address of Business.'
                            : 'Please enter a valid zip code';
                }
            }
        });
    });

    const fieldErrors = {};
    visibleResolvedPaths.forEach((resolvedPath) => {
        if (initialErrors[resolvedPath]) {
            fieldErrors[resolvedPath] = initialErrors[resolvedPath];
            return;
        }
        if (shownErrors[resolvedPath]) {
            fieldErrors[resolvedPath] = shownErrors[resolvedPath];
        }
    });

    return {
        fieldErrors,
        visibleResolvedPaths: Array.from(visibleResolvedPaths),
    };
};

export const calculateInitialErrors = ({ data, step, iPlusFields, setErrors, errors, type, shouldValidateField }) => {
    const { fieldErrors } = collectVisibleFieldErrors({ data, step, iPlusFields, errors, type, shouldValidateField });
    setErrors(() => fieldErrors);
};

/**
 * Check if account activity should block progression.
 */
export const shouldBlockAccountActivityProgression = (data) => {
    if (!data) return false;

    const requiredTransactionFields = [
        "Average_Balance",
        "Number_Of_Monthly_Ach_Deposits",
        "Amount_Of_Monthly_Ach_Deposits",
        "Number_Of_Monthly_Domestic_Wire_Deposits",
        "Amount_Of_Monthly_Domestic_Wire_Deposits",
    ];

    return requiredTransactionFields.some((field) => {
        const value = data[field];
        return value === undefined || value === null || value === "";
    });
};
